'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button, ModalHeader } from 'reactstrap';

import NotificationType from '../constants/notificationType';
import { memberService } from '../services/member.service';
import { openNotification } from '../utlis/notification';
import { validateName } from '../utlis/utils';
import ErrorBulkInviteMembers from './InviteMembersBulkError';

const InviteBulkMembers = (props) => {
  const { t } = useTranslation(['global', 'members', 'notifications']);
  const organization = useSelector((state) => state.organization);

  const [errorRows, setErrorRows] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [totalRows, setTotalRows] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setIsLoading] = useState(false);

  const handleProceed = async (file) => {
    setIsLoading(true);
    setErrorRows([]);
    setShowErrorModal(false);
    setTotalRows(null);

    try {
      const formData = new FormData();
      if (!file) {
        formData.append('file', selectedFile);
      } else {
        formData.append('file', file);
      }

      await memberService.inviteBulkTeamMembers(organization.id, formData);
      //TODO handle success
      openNotification(
        t('notifications:bulkCSVmemberInvited'),
        NotificationType.SUCCESS
      );
      setIsLoading(false);
      props.pushNewMember();
    } catch (error) {
      //TODO handle error. backend should validate the file. comes later
      openNotification(error.error, NotificationType.ERROR);
      setSelectedFile(null);
      setIsLoading(false);
    }
  };

  const handleFileChange = async (event) => {
    setIsLoading(true);
    const selectedFile = event.target.files[0];
    setSelectedFile(selectedFile);

    if (!selectedFile) {
      alert(t('notifications:noFileSelected'));
      return;
    }

    if (selectedFile.type !== 'text/csv') {
      alert(t('notifications:onlyCsvAllowed'));
      return;
    }

    const text = await selectedFile.text();
    const rows = text.trim().split('\n');
    const header = rows[0].split(',').map((col) => col.trim().toLowerCase());

    const requiredColumns = ['email', 'first_name', 'last_name', 'role'];
    const headerNormalized = header.map((col) => col.trim().toLowerCase());

    // Check that headers match exactly in length and order
    const headersMatch = requiredColumns.every(
      (col, index) => headerNormalized[index] === col
    );

    if (!headersMatch) {
      alert(t('members:invalidHeadersCsv'));
      return;
    }

    if (header.length < requiredColumns.length) {
      alert(t('members:fewerColumnsCsv'));
      return;
    }

    const dataRows = rows.slice(1);
    if (dataRows.length > 500) {
      alert(t('members:moreThan500Rows'));
      return;
    }

    const errorList = [];

    setTotalRows(dataRows.length);
    for (let i = 0; i < dataRows.length; i++) {
      const row = dataRows[i].split(',');
      const [email, first_name, last_name, role] = row.map((cell) =>
        cell.trim()
      );
      const rowIndex = i + 2;

      if (!email) {
        errorList.push({
          row: rowIndex,
          email: email || 'N/A',
          comment: t('notifications:missingRequiredFields'),
        });
        continue;
      }

      if (!last_name) {
        errorList.push({
          row: rowIndex,
          email: email || 'N/A',
          comment: t('notifications:missingRequiredFields'),
        });
        continue;
      }

      const isEmailValid = email
        .toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        );

      if (!isEmailValid) {
        errorList.push({
          row: rowIndex,
          email,
          comment: t('notifications:invalidEmail'),
        });
        continue;
      }

      if (!validateName(last_name)) {
        errorList.push({
          row: rowIndex,
          email,
          comment: t('notifications:invalidLastName'),
        });
        continue;
      }

      if (first_name && !validateName(first_name)) {
        errorList.push({
          row: rowIndex,
          email,
          comment: t('notifications:invalidFirstName'),
        });
        continue;
      }
    }

    if (errorList.length > 0) {
      setErrorRows(errorList);
      setShowErrorModal(true);
      setIsLoading(false);
      return;
    }
    await handleProceed(selectedFile);
  };

  const handleButtonClick = () => {
    document.getElementById('fileInput').click();
  };

  return (
    <>
      <div className="d-flex flex-column justify-content-center align-items-center pt-5 pr-4">
        <ModalHeader className="card-header">
          <h1>{t('members:errorBulkInviteModalTitle')}</h1>
        </ModalHeader>

        <div className="text-center mb-3">
          <p>
            {t('members:csvUploadInstructionMessage')}
            <br />
            {t('email, first_name, last_name, role')}
          </p>
        </div>

        <div className="text-center mb-3">
          <input
            id="fileInput"
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={async (e) => {
              await handleFileChange(e);
              e.target.value = '';
            }}
          />

          <Button
            color="primary"
            disabled={loading}
            onClick={handleButtonClick}
          >
            {t('members:uploadCsvButtonText')}{' '}
            {loading && <span className="spinner-border spinner-border-sm" />}
          </Button>
        </div>

        <div
          onClick={() =>
            window.open(t(`members:downloadExampleCsvLink`), '_blank')
          }
          style={{ cursor: 'pointer' }}
        >
          <span className="text-underline">
            {`${t('members:downloadExampleCsv')} `}
          </span>
        </div>
      </div>
      <ErrorBulkInviteMembers
        isOpen={showErrorModal}
        toggle={() => setShowErrorModal(false)}
        errorRows={errorRows}
        totalRows={totalRows}
        onProceed={handleProceed}
      />
    </>
  );
};

export default InviteBulkMembers;
